import { useCallback, useMemo } from 'react'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { useAppDispatch } from 'hooks/reduxHooks'
import { updateBnblOccupancies, updateBnblPackageView } from 'actions/BnblModalActions'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms, DEFAULT_OCCUPANCY_FOR_BNBL } from 'components/OfferPage/BnblModal/utils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isCustomerSupportMode } from 'selectors/featuresSelectors'
import { sum } from 'lib/array/arrayUtils'
import config from 'constants/config'

interface MappedProps {
  occupancies: Array<App.Occupants>
  packageView?: App.OfferPackageOptionView
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  canRedeemLuxPlusBenefits: boolean
  baseMemberPrice: number
  isCustomerSupportMode: boolean
}

interface Props {
  offer: App.Offer
  packageKey: string
}

function BnblPackageRooms({
  offer,
  occupancies,
  packageView,
  baseBnblPackageViews,
  canRedeemLuxPlusBenefits,
  baseMemberPrice,
  isCustomerSupportMode,
}: Props & MappedProps) {
  const dispatch = useAppDispatch()
  const numberOfRooms = occupancies.length
  const shouldPreventMultipleRoomsBooking = numberOfRooms >= 2 && canRedeemLuxPlusBenefits && baseMemberPrice > 0 && !isCustomerSupportMode
  const maxNumberOfRooms = shouldPreventMultipleRoomsBooking ? 2 : 10

  const effectiveOccupancies = useMemo(
    () => (occupancies.length === 0 ? [DEFAULT_OCCUPANCY_FOR_BNBL] : occupancies),
    [occupancies],
  )

  const capacities = packageView?.package?.roomRate?.capacities

  // Keep the site-wide default for an added room, but never seed it above what the room can sleep.
  const defaultRoomOccupants = useMemo<App.Occupants | undefined>(() => {
    if (!capacities?.length) return undefined
    const maxAdults = Math.max(...capacities.map(capacity => capacity.adults))
    return {
      ...config.search.defaultOccupants,
      adults: Math.max(1, Math.min(config.search.defaultOccupants.adults, maxAdults)),
    }
  }, [capacities])

  const includedGuests = packageView?.package?.roomRate?.includedGuests

  const onOccupanciesChange = useCallback(
    (next: Array<App.Occupants | App.RoomOccupants>) => {
      if (!packageView) return

      const sanitised: Array<App.Occupants> = next.map(({ adults, children, childrenAge, infants, teenagers }) => ({
        adults,
        children,
        childrenAge,
        infants,
        teenagers,
      }))

      dispatch(updateBnblOccupancies(sanitised, offer.id, packageView.package.uniqueKey))

      if (sanitised.length !== numberOfRooms) {
        const baseSelectedPackageView = baseBnblPackageViews.find(
          pkg => pkg.package.uniqueKey === packageView.package.uniqueKey,
        )
        if (baseSelectedPackageView) {
          const updatedPackageView = getBnblPackageViewByNumberOfRooms(baseSelectedPackageView, sanitised.length)
          dispatch(updateBnblPackageView(offer.id, updatedPackageView))
        }
      }
    },
    [baseBnblPackageViews, dispatch, numberOfRooms, offer.id, packageView],
  )

  return (
    <OccupancyPicker
      occupancies={effectiveOccupancies}
      onChange={onOccupanciesChange}
      capacities={capacities}
      maxChildAge={offer.property?.maxChildAge}
      maxInfantAge={offer.property?.maxInfantAge}
      maxTeenagerAge={offer.property?.maxTeenagerAge}
      childVariant="child-age"
      enableMultiRoom
      maxRooms={maxNumberOfRooms}
      saleUnit="room"
      includedGuests={includedGuests}
      defaultRoomOccupants={defaultRoomOccupants}
    />
  )
}

export default withOfferPageAndReduxState((offerPageState, appState: App.State, ownProps: Props): MappedProps => {
  const bnblModalPackages = appState.offer.bnblModal[ownProps.offer.id]
  const modalPackage = bnblModalPackages.find(p => p.packageView.package.uniqueKey === ownProps.packageKey)
  const baseMemberPrice = sum(bnblModalPackages, p => p.packageView.baseMemberPrice)

  return {
    baseMemberPrice,
    occupancies: modalPackage?.occupancies ?? [],
    packageView: modalPackage?.packageView,
    baseBnblPackageViews: getBnblPackageViews(offerPageState),
    canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
    isCustomerSupportMode: isCustomerSupportMode(appState),
  }
}, BnblPackageRooms)
